<template>
  <div class="json-editor">
    <div class="editor-layout">
      <!-- 左侧树导航 -->
      <div class="tree-sidebar">
        <div class="tree-header">
          <h3>导航</h3>
        </div>
        <div class="tree-content">
          <div v-if="!treeData || treeData.length === 0" class="empty-tree">
            <p>暂无数据</p>
          </div>
          <Tree
            v-else
            :data="treeData"
            @on-click="handleTreeClick"
            @on-select-change="handleTreeSelect"
            @click="handleTreeClick"
            @select-change="handleTreeSelect"
            class="navigation-tree"
            :show-checkbox="false"
            :check-strictly="false"
            :multiple="false"
          >
            <template slot="title" slot-scope="{ item }">
              <span class="tree-node-title" @click.stop="handleTreeClick(item)">
                <Icon :type="getNodeIcon(item)" />
                {{ item.title }}
              </span>
            </template>
          </Tree>
        </div>
      </div>

      <!-- 右侧编辑区域 -->
      <div class="editor-main" ref="editorMain">
        <div v-if="!jsonData.sections || jsonData.sections.length === 0" class="empty-state">
          <p>暂无数据，点击下方按钮添加段落</p>
          <Button type="primary" @click="addSection">添加段落</Button>
        </div>

        <div v-else class="sections-container">
        <div v-for="(section, sectionIndex) in jsonData.sections" :key="sectionIndex" class="section-item" :data-section="sectionIndex">
          <div class="section-header">
            <h3>段落 {{ sectionIndex + 1 }} : {{ section.name || '无' }}</h3>
            <div class="section-actions">
              <Button type="error" size="small" @click="removeSection(sectionIndex)">删除段落</Button>
            </div>
          </div>

          <div class="section-content">
            <!-- 段落名称 -->
            <div class="form-item">
              <label>段落名称:</label>
              <Input v-model="section.name" placeholder="请输入段落名称" @input="emitInput" />
            </div>

            <!-- 段落描述 -->
            <div class="form-item">
              <label>段落描述:</label>
              <Input
                v-model="section.desc"
                type="textarea"
                :rows="4"
                placeholder="请输入段落描述，支持换行符\\n、列表和缩进"
                @input="emitInput"
              />
            </div>

            <!-- 工作分组 -->
            <div class="groups-section">
              <div class="section-title">
                <h4>工作分组</h4>
                <Button type="primary" size="small" @click="addGroup(sectionIndex)">添加分组</Button>
              </div>

              <div v-for="(group, groupIndex) in section.groups" :key="groupIndex" class="group-item" :data-group="`${sectionIndex}-${groupIndex}`">
                <div class="group-header">
                  <h5>分组 {{ groupIndex + 1 }} : {{ group.name || '无' }}</h5>
                  <Button type="error" size="small" @click="removeGroup(sectionIndex, groupIndex)">删除分组</Button>
                </div>

                <div class="group-content">
                  <div class="form-item">
                    <label>分组名称:</label>
                    <Input v-model="group.name" placeholder="请输入分组名称" @input="emitInput" />
                  </div>

                  <!-- 任务列表 -->
                  <div class="tasks-section">
                    <div class="section-title">
                      <h6>任务列表</h6>
                      <Button type="primary" size="small" @click="addTask(sectionIndex, groupIndex)">添加任务</Button>
                    </div>

                    <div v-for="(task, taskIndex) in group.tasks" :key="taskIndex" class="task-item" :data-task="`${sectionIndex}-${groupIndex}-${taskIndex}`">
                      <div class="task-header">
                        <h6>任务 {{ taskIndex + 1 }} : {{ task.name || '无' }}</h6>
                        <Button type="error" size="small" @click="removeTask(sectionIndex, groupIndex, taskIndex)">删除任务</Button>
                      </div>

                      <div class="task-content">
                        <div class="form-item">
                          <label>任务ID:</label>
                          <Input disabled v-model="task.task_id" placeholder="请输入任务ID" @input="emitInput" />
                        </div>

                        <div class="form-item">
                          <label>任务名称:</label>
                          <Input v-model="task.name" placeholder="请输入任务名称" @input="emitInput" />
                        </div>

                        <div class="form-item">
                          <label>任务描述:</label>
                          <Input v-model="task.desc" type="textarea" :rows="3" placeholder="请输入任务描述" @input="emitInput" />
                        </div>

                        <!-- 照片列表 -->
                        <div class="media-section">
                          <div class="section-title">
                            <h6>现场照片</h6>
                            <Button type="primary" size="small" @click="addPhoto(sectionIndex, groupIndex, taskIndex)">添加照片</Button>
                          </div>
                          <div v-for="(photo, photoIndex) in task.photos" :key="photoIndex" class="media-item">
                            <div class="photo-preview">
                              <div v-if="photo.url" class="photo-image">
                                <img :src="getFullImageUrl(photo.url)" alt="现场照片" @click="previewImage(getFullImageUrl(photo.url))" />
                                <div class="photo-overlay">
                                  <Icon type="ios-eye" @click="previewImage(getFullImageUrl(photo.url))" title="预览" />
                                  <Icon type="ios-cloud-upload" @click="uploadPhoto(sectionIndex, groupIndex, taskIndex, photoIndex)" title="重新上传" />
                                </div>
                              </div>
                              <div v-else class="photo-upload-placeholder" @click="uploadPhoto(sectionIndex, groupIndex, taskIndex, photoIndex)">
                                <Icon type="ios-cloud-upload" />
                                <span>点击上传照片</span>
                              </div>
                            </div>
                            <div class="photo-info">
                              <div>照片名称</div> <Input v-model="photo.name" placeholder="照片名称" @input="emitInput" />
                              <Button type="error" size="small" style="width:80px" @click="removePhoto(sectionIndex, groupIndex, taskIndex, photoIndex)">删除</Button>
                            </div>
                          </div>
                        </div>

                        <!-- 附件列表 -->
                        <div class="media-section">
                          <div class="section-title">
                            <h6>附件</h6>
                            <Button type="primary" size="small" @click="addFile(sectionIndex, groupIndex, taskIndex)">添加附件</Button>
                          </div>
                          <div v-for="(file, fileIndex) in task.files" :key="fileIndex" class="media-item">
                            <Input v-model="file.url" placeholder="附件URL" style="margin-bottom: 5px;" @input="emitInput" />
                            <Input v-model="file.name" placeholder="附件名称" @input="emitInput" />
                            <Button type="error" size="small" @click="removeFile(sectionIndex, groupIndex, taskIndex, fileIndex)">删除</Button>
                          </div>
                        </div>

                        <!-- 电子工单 -->
                        <div class="media-section">
                          <div class="section-title">
                            <h6>电子工单</h6>
                            <Button type="primary" size="small" @click="addSheet(sectionIndex, groupIndex, taskIndex)">添加工单</Button>
                          </div>
                          <div v-for="(sheet, sheetIndex) in task.sheets" :key="sheetIndex" class="media-item">
                            <Input v-model="sheet.url" placeholder="工单URL" style="margin-bottom: 5px;" @input="emitInput" />
                            <Input v-model="sheet.name" placeholder="工单名称" @input="emitInput" />
                            <Button type="error" size="small" @click="removeSheet(sectionIndex, groupIndex, taskIndex, sheetIndex)">删除</Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 问题列表 -->
            <div class="questions-section">
              <div class="section-title">
                <h4>存在问题</h4>
                <Button type="primary" size="small" @click="addQuestion(sectionIndex)">添加问题</Button>
              </div>

              <div v-for="(question, questionIndex) in section.questions" :key="questionIndex" class="question-item" :data-question="`${sectionIndex}-${questionIndex}`">
                <div class="question-header">
                  <h5>问题 {{ questionIndex + 1 }}</h5>
                  <Button type="error" size="small" @click="removeQuestion(sectionIndex, questionIndex)">删除问题</Button>
                </div>

                <div class="question-content">
                  <div class="form-item">
                    <label>问题名称:</label>
                    <Input v-model="question.name" placeholder="请输入问题名称" @input="emitInput" />
                  </div>

                  <div class="form-item">
                    <label>问题描述:</label>
                    <Input v-model="question.desc" type="textarea" :rows="3" placeholder="请输入问题描述" @input="emitInput" />
                  </div>

                  <!-- 问题照片 -->
                  <div class="media-section">
                    <div class="section-title">
                      <h6>现场照片</h6>
                      <Button type="primary" size="small" @click="addQuestionPhoto(sectionIndex, questionIndex)">添加照片</Button>
                    </div>
                    <div v-for="(photo, photoIndex) in question.photos" :key="photoIndex" class="media-item">
                      <div class="photo-preview">
                        <div v-if="photo.url" class="photo-image">
                          <img :src="getFullImageUrl(photo.url)" alt="现场照片" @click="previewImage(getFullImageUrl(photo.url))" />
                          <div class="photo-overlay">
                            <Icon type="ios-eye" @click="previewImage(getFullImageUrl(photo.url))" title="预览" />
                            <Icon type="ios-cloud-upload" @click="uploadQuestionPhoto(sectionIndex, questionIndex, photoIndex)" title="重新上传" />
                          </div>
                        </div>
                        <div v-else class="photo-upload-placeholder" @click="uploadQuestionPhoto(sectionIndex, questionIndex, photoIndex)">
                          <Icon type="ios-cloud-upload" />
                          <span>点击上传照片</span>
                        </div>
                      </div>
                      <div class="photo-info">
                        <div>照片名称</div>
                        <Input v-model="photo.name" placeholder="照片名称" @input="emitInput" />
                        <Button type="error" size="small" style="width:80px" @click="removeQuestionPhoto(sectionIndex, questionIndex, photoIndex)">删除</Button>
                      </div>
                    </div>
                  </div>

                  <!-- 问题整改状态 -->
                  <div class="status-section">
                    <h6>整改状态</h6>
                    <div class="form-item">
                      <label>整改状态:</label>
                      <Select v-model="question.status" @on-change="emitInput">
                        <Option :value="0">整改中</Option>
                        <Option :value="1">已整改</Option>
                      </Select>
                    </div>
                  </div>

                  <!-- 整改回复 -->
                  <div class="responses-section">
                    <div class="section-title">
                      <h6>整改回复</h6>
                      <Button type="primary" size="small" @click="addResponse(sectionIndex, questionIndex)">添加回复</Button>
                    </div>
                    <div v-for="(response, responseIndex) in question.responses" :key="responseIndex" class="response-item">
                      <div class="response-header">
                        <h6>回复 {{ responseIndex + 1 }}</h6>
                        <Button type="error" size="small" @click="removeResponse(sectionIndex, questionIndex, responseIndex)">删除回复</Button>
                      </div>
                      <div class="response-content">
                        <div class="form-item">
                          <label>回复日期:</label>
                          <DatePicker v-model="response.date" type="date" placeholder="选择日期" @on-change="emitInput" />
                        </div>
                        <div class="form-item">
                          <label>整改回复进展描述:</label>
                          <Input v-model="response.desc" type="textarea" :rows="3" placeholder="请输入整改回复进展描述" @input="emitInput" />
                        </div>
                        
                        <!-- 整改照片 -->
                        <div class="media-section">
                          <div class="section-title">
                            <h6>整改照片</h6>
                            <Button type="primary" size="small" @click="addResponsePhoto(sectionIndex, questionIndex, responseIndex)">添加照片</Button>
                          </div>
                          <div v-for="(photo, photoIndex) in response.photos" :key="photoIndex" class="media-item">
                            <div class="photo-preview">
                              <div v-if="photo.url" class="photo-image">
                                <img :src="getFullImageUrl(photo.url)" alt="整改照片" @click="previewImage(getFullImageUrl(photo.url))" />
                                <div class="photo-overlay">
                                  <Icon type="ios-eye" @click="previewImage(getFullImageUrl(photo.url))" title="预览" />
                                  <Icon type="ios-cloud-upload" @click="uploadResponsePhoto(sectionIndex, questionIndex, responseIndex, photoIndex)" title="重新上传" />
                                </div>
                              </div>
                              <div v-else class="photo-upload-placeholder" @click="uploadResponsePhoto(sectionIndex, questionIndex, responseIndex, photoIndex)">
                                <Icon type="ios-cloud-upload" />
                                <span>点击上传照片</span>
                              </div>
                            </div>
                            <div class="photo-info">
                              <div>照片名称</div>
                              <Input v-model="photo.name" placeholder="照片名称" @input="emitInput" />
                              <Button type="error" size="small" style="width:80px" @click="removeResponsePhoto(sectionIndex, questionIndex, responseIndex, photoIndex)">删除</Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 联系单 -->
                  <div class="sheet-section" v-if="question.sheet">
                    <h6>联系单</h6>
                    <div class="form-item">
                      <label>发送日期:</label>
                      <DatePicker v-model="question.sheet['sheet-date']" type="date" placeholder="选择日期" @on-change="emitInput" />
                    </div>
                    <div class="form-item">
                      <label>整改状态:</label>
                      <Select v-model="question.sheet['sheet-state']" @on-change="emitInput">
                        <Option value="pending">未整改</Option>
                        <Option value="completed">已整改</Option>
                      </Select>
                    </div>
                  </div>

                  <div v-else>
                    <Button type="primary" size="small" @click="addQuestionSheet(sectionIndex, questionIndex)">添加联系单</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        <div class="add-section">
          <Button type="primary" @click="addSection">添加段落</Button>
        </div>
      </div>
    </div>

    <!-- 隐藏的文件上传输入框 -->
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      style="display: none;"
      @change="handleFileUpload"
    />
  </div>
</template>

<script>
export default {
  name: 'ReportEditor',
  props: {
    value: {
      type: String,
      default: '{"sections": []}',
    },
  },
  data() {
    return {
      jsonData: {sections: []},
      selectedNode: null,
      emitTimer: null,
      isUpdatingFromValue: false,
      currentUploadTarget: null, // 记录当前上传目标
    };
  },
  watch: {
    value: {
      handler(newVal) {
        this.isUpdatingFromValue = true;
        if (!newVal) {
          this.jsonData = {sections: []};
        } else {
          try {
            this.jsonData = JSON.parse(newVal || '{"sections": []}');
          } catch (error) {
            console.error('JSON解析错误:', error);
            this.jsonData = {sections: []};
          }
        }
        this.$nextTick(() => {
          this.isUpdatingFromValue = false;
        });
      },
      immediate: true,
    },
    jsonData: {
      handler(newVal) {
        // 当jsonData发生变化时，只同步数据，不emit
        // 避免从value更新时触发循环
        if (!this.isUpdatingFromValue) {
          // 这里可以添加其他需要的数据同步逻辑
        }
      },
      deep: true,
    },
  },
  computed: {
    treeData() {
      return this.generateTreeData();
    },
  },
  methods: {
    // 通用方法：emit input事件（带防抖）
    emitInput() {
      // 清除之前的定时器
      if (this.emitTimer) {
        clearTimeout(this.emitTimer);
      }

      // 设置新的定时器，300ms后执行
      this.emitTimer = setTimeout(() => {
        try {
          const jsonString = JSON.stringify(this.jsonData, null, 2);
          this.$emit('input', jsonString);
        } catch (error) {
          console.error('emit input时JSON序列化错误:', error);
        }
      }, 300);
    },

    saveChanges() {
      try {
        const jsonString = JSON.stringify(this.jsonData, null, 2);
        this.$emit('on-submit', jsonString);
        this.$Message.success('保存成功');
      } catch (error) {
        console.error('保存时JSON序列化错误:', error);
        this.$Message.error('保存失败：数据格式错误');
      }
    },

    cancelEdit() {
      this.$emit('on-cancel');
    },

    resetData() {
      try {
        this.jsonData = JSON.parse(this.value || '{"sections": []}');
        this.$Message.success('数据已重置');
      } catch (error) {
        console.error('重置数据时JSON解析错误:', error);
        this.jsonData = {sections: []};
        this.$Message.error('重置失败：数据格式错误');
      }
    },

    // 段落操作
    addSection() {
      this.jsonData.sections.push({
        name: '',
        desc: '',
        groups: [],
        questions: [],
      });
      this.emitInput();
    },

    removeSection(sectionIndex) {
      this.jsonData.sections.splice(sectionIndex, 1);
      this.emitInput();
    },

    // 分组操作
    addGroup(sectionIndex) {
      if (!this.jsonData.sections[sectionIndex].groups) {
        this.$set(this.jsonData.sections[sectionIndex], 'groups', []);
      }
      this.jsonData.sections[sectionIndex].groups.push({
        name: '',
        tasks: [],
      });
      this.emitInput();
    },

    removeGroup(sectionIndex, groupIndex) {
      this.jsonData.sections[sectionIndex].groups.splice(groupIndex, 1);
      this.emitInput();
    },

    // 任务操作
    addTask(sectionIndex, groupIndex) {
      if (!this.jsonData.sections[sectionIndex].groups[groupIndex].tasks) {
        this.$set(this.jsonData.sections[sectionIndex].groups[groupIndex], 'tasks', []);
      }
      this.jsonData.sections[sectionIndex].groups[groupIndex].tasks.push({
        task_id: '',
        name: '',
        desc: '',
        photos: [],
        files: [],
        sheets: [],
      });
      this.emitInput();
    },

    removeTask(sectionIndex, groupIndex, taskIndex) {
      this.jsonData.sections[sectionIndex].groups[groupIndex].tasks.splice(taskIndex, 1);
      this.emitInput();
    },

    // 照片操作
    addPhoto(sectionIndex, groupIndex, taskIndex) {
      if (!this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex].photos) {
        this.$set(this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex], 'photos', []);
      }
      this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex].photos.push({
        url: '',
        name: '',
      });
      this.emitInput();
    },

    removePhoto(sectionIndex, groupIndex, taskIndex, photoIndex) {
      this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex].photos.splice(photoIndex, 1);
      this.emitInput(); // 修复：删除后触发数据更新
    },

    // 附件操作
    addFile(sectionIndex, groupIndex, taskIndex) {
      if (!this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex].files) {
        this.$set(this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex], 'files', []);
      }
      this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex].files.push({
        url: '',
        name: '',
      });
      this.emitInput();
    },

    removeFile(sectionIndex, groupIndex, taskIndex, fileIndex) {
      this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex].files.splice(fileIndex, 1);
      this.emitInput();
    },

    // 工单操作
    addSheet(sectionIndex, groupIndex, taskIndex) {
      if (!this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex].sheets) {
        this.$set(this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex], 'sheets', []);
      }
      this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex].sheets.push({
        url: '',
        name: '',
      });
      this.emitInput();
    },

    removeSheet(sectionIndex, groupIndex, taskIndex, sheetIndex) {
      this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex].sheets.splice(sheetIndex, 1);
      this.emitInput();
    },

    // 问题操作
    addQuestion(sectionIndex) {
      if (!this.jsonData.sections[sectionIndex].questions) {
        this.$set(this.jsonData.sections[sectionIndex], 'questions', []);
      }
      this.jsonData.sections[sectionIndex].questions.push({
        name: '',
        desc: '',
        photos: [],
        status: 0, // 默认整改中
        responses: [],
      });
      this.emitInput();
    },

    removeQuestion(sectionIndex, questionIndex) {
      this.jsonData.sections[sectionIndex].questions.splice(questionIndex, 1);
      this.emitInput();
    },

    // 问题照片操作
    addQuestionPhoto(sectionIndex, questionIndex) {
      if (!this.jsonData.sections[sectionIndex].questions[questionIndex].photos) {
        this.$set(this.jsonData.sections[sectionIndex].questions[questionIndex], 'photos', []);
      }
      this.jsonData.sections[sectionIndex].questions[questionIndex].photos.push({
        url: '',
        name: '',
      });
      this.emitInput();
    },

    removeQuestionPhoto(sectionIndex, questionIndex, photoIndex) {
      this.jsonData.sections[sectionIndex].questions[questionIndex].photos.splice(photoIndex, 1);
      this.emitInput(); // 修复：删除后触发数据更新
    },

    // 联系单操作
    addQuestionSheet(sectionIndex, questionIndex) {
      this.$set(this.jsonData.sections[sectionIndex].questions[questionIndex], 'sheet', {
        "sheet-date": moment().format("YYYY-MM-DD"),
        "sheet-state": 0,
      });
      this.emitInput();
    },

    // 整改回复操作
    addResponse(sectionIndex, questionIndex) {
      if (!this.jsonData.sections[sectionIndex].questions[questionIndex].responses) {
        this.$set(this.jsonData.sections[sectionIndex].questions[questionIndex], 'responses', []);
      }
      this.jsonData.sections[sectionIndex].questions[questionIndex].responses.push({
        date: '',
        desc: '',
        photos: [],
      });
      this.emitInput();
    },

    removeResponse(sectionIndex, questionIndex, responseIndex) {
      this.jsonData.sections[sectionIndex].questions[questionIndex].responses.splice(responseIndex, 1);
      this.emitInput();
    },

    // 整改照片操作
    addResponsePhoto(sectionIndex, questionIndex, responseIndex) {
      if (!this.jsonData.sections[sectionIndex].questions[questionIndex].responses[responseIndex].photos) {
        this.$set(this.jsonData.sections[sectionIndex].questions[questionIndex].responses[responseIndex], 'photos', []);
      }
      this.jsonData.sections[sectionIndex].questions[questionIndex].responses[responseIndex].photos.push({
        url: '',
        name: '',
      });
      this.emitInput();
    },

    removeResponsePhoto(sectionIndex, questionIndex, responseIndex, photoIndex) {
      this.jsonData.sections[sectionIndex].questions[questionIndex].responses[responseIndex].photos.splice(photoIndex, 1);
      this.emitInput(); // 修复：删除后触发数据更新
    },

    // 文件上传相关方法
    uploadPhoto(sectionIndex, groupIndex, taskIndex, photoIndex) {
      console.log('uploadPhoto', sectionIndex, groupIndex, taskIndex, photoIndex);
      this.currentUploadTarget = {sectionIndex, groupIndex, taskIndex, photoIndex, type: 'task'};
      this.$refs.fileInput.click();
    },

    uploadQuestionPhoto(sectionIndex, questionIndex, photoIndex) {
      console.log('uploadQuestionPhoto', sectionIndex, questionIndex, photoIndex);
      this.currentUploadTarget = {sectionIndex, questionIndex, photoIndex, type: 'question'};
      this.$refs.fileInput.click();
    },

    uploadResponsePhoto(sectionIndex, questionIndex, responseIndex, photoIndex) {
      console.log('uploadResponsePhoto', sectionIndex, questionIndex, responseIndex, photoIndex);
      this.currentUploadTarget = {sectionIndex, questionIndex, responseIndex, photoIndex, type: 'response'};
      this.$refs.fileInput.click();
    },

    // 处理图片URL，如果是不完整的URL则添加域名前缀
    getFullImageUrl(url) {
      if (!url) return '';
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      return `https://www.gaozhuan.net${url.startsWith('/') ? '' : '/'}${url}`;
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!this.currentUploadTarget) return;
      console.log(this.currentUploadTarget);

      // 保存当前上传目标，避免在回调中丢失
      const uploadTarget = {...this.currentUploadTarget};

      // 使用上传服务
      this.$cos.upload(
          [file],
          {
            onFail: (e) => {
              console.log(e);
              this.$Message.error('文件上传失败');
            },
            onProgress: (progressEvent) => {
            // 可以在这里显示上传进度
            },
            onSuccess: (files) => {
              if (!Array.isArray(files) || files.length === 0) {
                this.$Message.error('文件上传失败');
                return;
              }

              const uploadedUrl = files[0].url;
              const fileName = file.name || '上传的图片';

              console.log('onSuccess', uploadTarget);

              // 根据上传目标类型更新对应的数据
              if (uploadTarget.type === 'task') {
                const {sectionIndex, groupIndex, taskIndex, photoIndex} = uploadTarget;
                this.$set(this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex].photos[photoIndex], 'url', uploadedUrl);
                this.$set(this.jsonData.sections[sectionIndex].groups[groupIndex].tasks[taskIndex].photos[photoIndex], 'name', fileName);
              } else if (uploadTarget.type === 'question') {
                const {sectionIndex, questionIndex, photoIndex} = uploadTarget;
                this.$set(this.jsonData.sections[sectionIndex].questions[questionIndex].photos[photoIndex], 'url', uploadedUrl);
                this.$set(this.jsonData.sections[sectionIndex].questions[questionIndex].photos[photoIndex], 'name', fileName);
              } else if (uploadTarget.type === 'response') {
                const {sectionIndex, questionIndex, responseIndex, photoIndex} = uploadTarget;
                this.$set(this.jsonData.sections[sectionIndex].questions[questionIndex].responses[responseIndex].photos[photoIndex], 'url', uploadedUrl);
                this.$set(this.jsonData.sections[sectionIndex].questions[questionIndex].responses[responseIndex].photos[photoIndex], 'name', fileName);
              }

              this.emitInput();
              this.$Message.success('文件上传成功');
            },
          },
          {coskey: this.$store.getters['session/session'].coskey},
      );

      // 清空文件输入框
      event.target.value = '';
      this.currentUploadTarget = null;
    },

    // 图片预览功能
    previewImage(url) {
      if (!url) return;

      // 创建预览模态框
      this.$Modal.info({
        title: '图片预览',
        content: `<div style="text-align: center;"><img src="${url}" style="max-width: 100%; max-height: 500px;" /></div>`,
        width: 800,
        closable: true,
      });
    },

    // 树导航相关方法
    generateTreeData() {
      const treeData = [];

      if (!this.jsonData || !this.jsonData.sections) {
        return treeData;
      }

      this.jsonData.sections.forEach((section, sectionIndex) => {
        const sectionNode = {
          title: section.name || `段落 ${sectionIndex + 1}`,
          expand: true,
          type: 'section',
          sectionIndex,
          children: [],
          id: `section-${sectionIndex}`,
        };

        // 添加分组
        if (section.groups && section.groups.length > 0) {
          section.groups.forEach((group, groupIndex) => {
            const groupNode = {
              title: group.name || `分组 ${groupIndex + 1}`,
              expand: true,
              type: 'group',
              sectionIndex,
              groupIndex,
              children: [],
              id: `group-${sectionIndex}-${groupIndex}`,
            };

            // 添加任务
            if (group.tasks && group.tasks.length > 0) {
              group.tasks.forEach((task, taskIndex) => {
                const taskNode = {
                  title: task.name || task.task_id || `任务 ${taskIndex + 1}`,
                  expand: false,
                  type: 'task',
                  sectionIndex,
                  groupIndex,
                  taskIndex,
                  id: `task-${sectionIndex}-${groupIndex}-${taskIndex}`,
                };
                groupNode.children.push(taskNode);
              });
            }

            sectionNode.children.push(groupNode);
          });
        }

        // 添加问题
        if (section.questions && section.questions.length > 0) {
          section.questions.forEach((question, questionIndex) => {
            const questionNode = {
              title: question.name || `问题 ${questionIndex + 1}`,
              expand: false,
              type: 'question',
              sectionIndex,
              questionIndex,
              id: `question-${sectionIndex}-${questionIndex}`,
            };
            sectionNode.children.push(questionNode);
          });
        }

        treeData.push(sectionNode);
      });

      return treeData;
    },

    handleTreeSelect(selectedKeys, selectedNodes) {
      if (selectedNodes && typeof selectedNodes === 'object' && selectedNodes.type) {
        this.selectedNode = selectedNodes;
        this.scrollToElement(selectedNodes);
      }
    },

    handleTreeClick(item) {
      if (item && item.type) {
        this.selectedNode = item;
        this.scrollToElement(item);
      }
    },


    scrollToElement(node) {
      this.$nextTick(() => {
        let element = null;
        let selector = '';

        switch (node.type) {
          case 'section':
            selector = `[data-section="${node.sectionIndex}"]`;
            break;
          case 'group':
            selector = `[data-group="${node.sectionIndex}-${node.groupIndex}"]`;
            break;
          case 'task':
            selector = `[data-task="${node.sectionIndex}-${node.groupIndex}-${node.taskIndex}"]`;
            break;
          case 'question':
            selector = `[data-question="${node.sectionIndex}-${node.questionIndex}"]`;
            break;
        }

        if (selector) {
          element = document.querySelector(selector);
        }

        if (!this.$refs.editorMain || !element) {
          return;
        }

        // 使用ref获取编辑器主容器
        const editorMain = this.$refs.editorMain;

        // 计算元素在容器内的位置
        const elementRect = element.getBoundingClientRect();
        const containerRect = editorMain.getBoundingClientRect();

        // 计算需要滚动的距离
        const scrollTop = editorMain.scrollTop + (elementRect.top - containerRect.top) - 20;
        const targetScrollTop = Math.max(0, scrollTop);

        // 滚动到指定位置
        try {
          editorMain.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth',
          });

          // 备用滚动方法
          setTimeout(() => {
            if (Math.abs(editorMain.scrollTop - targetScrollTop) > 10) {
              editorMain.scrollTop = targetScrollTop;
            }
          }, 100);
        } catch (error) {
          editorMain.scrollTop = targetScrollTop;
        }

        // 高亮显示
        element.style.backgroundColor = '#e6f7ff';
        setTimeout(() => {
          element.style.backgroundColor = '';
        }, 2000);
      });
    },

    getNodeIcon(item) {
      switch (item.type) {
        case 'section':
          return 'ios-folder';
        case 'group':
          return 'ios-people';
        case 'task':
          return 'ios-list';
        case 'question':
          return 'ios-help-circle';
        default:
          return 'ios-document';
      }
    },
  },
};
</script>

<style scoped>
.json-editor {
  height: 100%;
  background: #f8f9fa;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #e8eaec;
  border-radius: 8px;
}

.editor-header {
  margin-bottom: 20px;
  padding: 15px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.editor-layout {
  display: flex;
  gap: 20px;
  flex: 1;
  overflow: hidden;
}

.tree-sidebar {
  width: 380px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
  flex-shrink: 0;
  min-width: 380px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tree-header {
  padding: 15px 20px;
  border-bottom: 1px solid #e8eaec;
  background: #f8f9fa;
}

.tree-header h3 {
  margin: 0;
  color: #17233d;
  font-size: 16px;
}

.tree-content {
  padding: 15px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.empty-tree {
  text-align: center;
  color: #999;
  padding: 20px;
}

.navigation-tree {
  border: none;
}

.tree-node-title {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  word-wrap: break-word;
  word-break: break-all;
  padding-right: 10px;
  flex-wrap: wrap;
  min-height: 20px;
  width: 100%;
  max-width: 100%;
}

/* 确保树节点文本能够换行 */
.navigation-tree .ivu-tree-title {
  word-wrap: break-word;
  word-break: break-all;
  white-space: normal;
  line-height: 1.4;
  padding-right: 10px;
  max-width: 100%;
  overflow-wrap: break-word;
  display: block;
  width: 100%;
}

/* 确保树节点容器也能正确处理换行 */
.navigation-tree .ivu-tree-node {
  word-wrap: break-word;
  overflow-wrap: break-word;
  width: 100%;
}

/* 确保树节点内容区域能够换行 */
.navigation-tree .ivu-tree-node-content {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  line-height: 1.4;
  padding-right: 10px;
  flex: 1;
  min-width: 0;
}

/* 确保图标和文本的对齐 */
.navigation-tree .ivu-tree-node-content-wrapper {
  display: flex;
  align-items: flex-start;
  padding: 5px 0;
  width: 100%;
}



/* 确保树节点展开/收起图标正确显示 */
.navigation-tree .ivu-tree-arrow {
  flex-shrink: 0;
}

/* 确保树节点可以正确点击 */
.navigation-tree .ivu-tree-title {
  cursor: pointer;
  user-select: none;
  
}

.navigation-tree .ivu-tree-node-content-wrapper {
  cursor: pointer;
}

.navigation-tree .ivu-tree-node-content-wrapper:hover {
  background-color: #f8f9fa;
}

.editor-main {
  flex: 1;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
  height: 100%;
  overflow-y: auto;
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
  color: #666;
}

.sections-container {
  padding: 20px;
}

.section-item {
  margin-bottom: 30px;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8eaec;
}

.section-header h3 {
  margin: 0;
  color: #17233d;
}

.section-content {
  padding: 20px;
}

.form-item {
  margin-bottom: 15px;
}

.form-item label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #17233d;
}

.section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #e8eaec;
}

.section-title h4, .section-title h6 {
  margin: 0;
  color: #17233d;
  font-size:14px;
}

.group-item, .task-item, .question-item {
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #e8eaec;
  border-radius: 6px;
  background: #fafbfc;
}

.group-header, .task-header, .question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  
  border-bottom: 1px solid #e8eaec;
}

.group-header h5, .task-header h6, .question-header h5 {
  margin: 0;
  color:var(--primary);
  font-size:12px;
}

.media-section {
  margin-top: 15px;
}


.sheet-section {
  margin-top: 15px;
  padding: 15px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e8eaec;
}

.add-section {
  text-align: center;
  padding: 20px;
  border: 2px dashed #e8eaec;
  border-radius: 8px;
  margin:0 20px;
  
}

.groups-section, .questions-section {
  margin-top: 20px;
}

.tasks-section {
  margin-top: 15px;
}

.flex-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.input-with-icon {
  position: relative;
  display: inline-block;
  width: 100%;
}

.photo-preview {
  margin-bottom: 10px;
}

.photo-image {
  position: relative;
  width: 120px;
  height: 120px;
  border: 1px solid #e8eaec;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
}

.photo-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.3s;
}

.photo-image:hover .photo-overlay {
  opacity: 1;
}

.photo-overlay .ivu-icon {
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 5px;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.3);
}

.photo-overlay .ivu-icon:hover {
  background: rgba(0, 0, 0, 0.6);
}

.photo-upload-placeholder {
  width: 120px;
  height: 120px;
  border: 2px dashed #e8eaec;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  transition: all 0.3s;
}

.photo-upload-placeholder:hover {
  border-color: #2d8cf0;
  color: #2d8cf0;
}

.photo-upload-placeholder .ivu-icon {
  font-size: 24px;
  margin-bottom: 5px;
}

.photo-upload-placeholder span {
  font-size: 12px;
}

.photo-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
}

.media-item {
  display: flex;
  gap: 15px;
  align-items: flex-start;
  margin-bottom: 15px;
  padding: 15px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e8eaec;
}

.status-section {
  margin-top: 15px;
  padding: 15px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e8eaec;
}

.responses-section {
  margin-top: 15px;
}

.response-item {
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #e8eaec;
  border-radius: 6px;
  background: #fafbfc;
}

.response-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e8eaec;
}

.response-header h6 {
  margin: 0;
  color: var(--primary);
  font-size: 12px;
}

.response-content {
  padding: 10px 0;
}
</style>
